/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Link,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { CollectionOrderBasicInfo } from "../components-organisms/CollectionOrderBasicInfo";
import { InfoSection } from "../components-molecules/InfoSection";
import { CollectionOrderHeader } from "../components-organisms/CollectionOrderHeader";
import CollectionOrderTotal from "../components-organisms/CollectionOrderTotal";
import {
  clearCollectionDetails,
  GetCollectionOrderDetail,
  ICollectOrderDetailResponseModel,
  ProofOfDelivery,
  ProofOfDeliveryListItem,
} from "../redux/actions/collectionOrderDetail";
import { CollectionOrderHistory } from "../components-molecules/CollectionOrderHistory";
import { OrderRejectButton, PrimaryButton } from "../components-atoms";
import {
  cancelOrderCreation,
  getQuote,
  placeCollectOrder,
} from "../redux/actions/collectOrders";
import { CancelCollectOrderModal } from "../components-molecules/CancelCollectOrderModal";
import { is_able_to } from "../utils/roles";
import { ErrorModal } from "../components-molecules";
import CollectionOrderDeliveryInfo from "../components-organisms/CollectionOrderDeliveryInfo";
import { Caption } from "../components-atoms/Caption";
import { CONTACT_PHONE } from "../components-molecules/VehicleTypeSelector";
import { colours, theme } from "../constants/theme";
import { CollectionOrderTotalSource, NewCollectionOrderTotal } from "../components-organisms/NewCollectionOrderTotal";
import { getMultipleValuesAsText } from "../utils/getMultipleValuesAsText";
import { useAppWebSocket } from "../hooks/useAppWebSocket";
import { CollectPricing } from "../redux/reducers/orderDetails";
import { useLocation } from "react-router-dom";
import CollectionOrderHeaderActions from "../components-molecules/CollectionOrderHeaderActions";
import { CollectOrderCanceledModal } from "../components-molecules/CollectOrderCanceledModal";
import { InfoSectionContainer } from "../components-molecules/InfoSectionContainer";

const useStyles = makeStyles(() => ({
  root: {
    overflow: "visible",
    paddingLeft: 0,
    paddingRight: 0,
    "@media (min-width: 900px)": {
      maxWidth: "840px",
    },
  },
  actionButtonsContainer: {
    display: 'flex'
  },
  imageText: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  infoText: {
    fontWeight: "normal",
    color: colours.highEmphasis54,
  },
  imagePreview: {
    width: '128px',
    height: '128px',
    objectFit: 'contain',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 4
  }
}));

type Steps =
  | "Ordered"
  | "Queued"
  | "Dispatched"
  | "Out for Delivery"
  | "Delivered"
  | "Failed"
  | "Cancelled";
export interface DetailsSocketMessage {
  DriverInformation?: {
    Name: string;
    LastUpdate?: string;
  };
  FleetInformation?: {
    Name: string;
    LogoUrl?: string;
  };
  ETA?: string;
  Status: {
    Step: Steps;
    DeliveredDate?: string;
    FailedDate?: string;
    CancelledDate?: string;
  };
  ProofOfDelivery?: ProofOfDelivery;
  ProofOfDeliveryList?: ProofOfDeliveryListItem[];
  WinningQuote?: CollectPricing;
  MapWidgetUrl: string | null;
}

export function CollectionOrder() {
  const { id }: { id: string } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const classes = useStyles();
  const role = useSelector((state: any) => state.login.role);
  const isAdmin = useMemo(() => role === "Admin", [role]);
  const [data, loading]: [ICollectOrderDetailResponseModel, boolean] =
    useSelector((state: any) => [
      state.collectionOrderDetail.data,
      state.collectionOrderDetail.loading,
    ]);

  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);

  const { lastJsonMessage } = useAppWebSocket<DetailsSocketMessage | null>(
    "/ws/collect-details",
    { queryParams: { pcid: id as string } },
    Boolean(id)
  );

  // Modals
  const [cancelOrderModalVisible, setCancelOrderModalVisible] =
    useState(false);
  const [cancelOrderErrorModalVisible, setCancelOrderErrorModalVisible] =
    useState(false);
  const [cancelOrderErrorModalText, setCancelOrderErrorModalText] =
    useState("");

  const showCancelBtn =
    (lastJsonMessage?.Status.Step === 'Ordered' ||
      lastJsonMessage?.Status.Step === 'Queued') &&
    !lastJsonMessage?.DriverInformation?.Name;
  const showActionButtons = showCancelBtn && is_able_to("reject_orders");


  // TK Premium
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
  const [getPriceLoading, setGetPriceLoading] = useState<boolean>(false);
  const [quoteIds, setQuoteIds] = useState([]);
  const [maxPriceQuoteId, setMaxPriceQuoteId] = useState<string | null>(null);
  const [showCanceledModal, setShowCanceledModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(GetCollectionOrderDetail(id));
    }
    return () => {
      dispatch(clearCollectionDetails());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (lastJsonMessage?.Status?.Step === 'CancelledByUser') {
      setShowCanceledModal(true);
    }
  }, [lastJsonMessage?.Status]);

  const onGetPrice = () => {
    setGetPriceLoading(true);
    getQuote(
      { orderId: data.Id },
      () => {
      },
      (err: any) => {
        // console.log("Err:", err) - removed
      }
    );
  };

  const handlePlaceOrder = () => {
    setPlaceOrderLoading(true);
    placeCollectOrder(
      {
        Id: data.Id,
        SelectedQuoteIds: quoteIds,
        MaxPriceQuoteId: maxPriceQuoteId,
      },
      () => {
        window.location.reload();
      },
      () => {
        setPlaceOrderLoading(false);
      }
    );
  };

  const renderCustomerInfo = useCallback(
    () => (
      <InfoSection
        title="Delivery Contact Details"
        sx={{ mb: 2, mt: 2 }}
        data={[
          {
            title: "Name",
            value: `${data.CustomerFirstName} ${data.CustomerLastName}`,
          },
          {
            title: "Email",
            value: data.CustomerEmail,
          },
          {
            title: "Phone",
            value: data.CustomerTelNo,
          },
        ]}
      />
    ),
    [data]
  );

  const proofOfDeliveryData = useCallback(
    (jsonMsg: DetailsSocketMessage | undefined | null) => {
      const hasData = jsonMsg?.ProofOfDelivery || (jsonMsg?.ProofOfDeliveryList?.length && jsonMsg?.ProofOfDeliveryList?.length > 0);
      if (!hasData) {
        return (
          <Typography style={{ marginTop: "16px" }}>
            The courier who completed this delivery did not
            automatically provide us with a copy of the POD. If
            you require it, please{" "}
            <Link
              href={`tel:${CONTACT_PHONE.replace(/ /g, "")}`}
              style={{ color: theme.palette.primary.main }}
            >
              contact the Live Operations team
            </Link>{" "}
            who can attempt to secure it.
          </Typography>
        )
      }
      return (
        <Box display="flex" flexDirection="row" style={{ gap: "16px", marginTop: "16px" }}>
          {jsonMsg?.ProofOfDelivery?.ImageUrl && (
            <Box className={classes.imageText}>
              <a href={jsonMsg?.ProofOfDelivery?.ImageUrl} target="_blank" rel="noreferrer">
                <img
                  src={jsonMsg?.ProofOfDelivery?.ImageUrl}
                  alt={jsonMsg?.ProofOfDelivery?.ImageName}
                  className={classes.imagePreview}
                />
              </a>
              <Typography variant="body1">{jsonMsg?.ProofOfDelivery?.ImageName}</Typography>
              <Caption display="block" className={classes.infoText}>
                Click to see a larger version.
              </Caption>
            </Box>
          )}
          {jsonMsg?.ProofOfDeliveryList?.map((item, idx) => (
            <Box className={classes.imageText} key={item.ImageUrl || idx}>
              <a href={item.ImageUrl} target="_blank" rel="noreferrer">
                <img src={item.ImageUrl} alt={item.ImageType} className={classes.imagePreview} />
              </a>
              <Typography variant="body1">
                {item.ImageType}
              </Typography>
              <Caption
                display="block"
                className={classes.infoText}
              >
                Click to see a larger version.
              </Caption>
            </Box>
          ))}
        </Box>
      )
    },
    [classes.imagePreview, classes.imageText, classes.infoText]
  );

  const renderProofOfDelivery = useCallback(
    () => (
      <InfoSectionContainer title="Proof of delivery" sx={{ mb: 2 }}>
        {proofOfDeliveryData(lastJsonMessage)}
      </InfoSectionContainer>
    ),
    [lastJsonMessage, proofOfDeliveryData]
  );

  const renderPricing = useCallback(
    () =>
      lastJsonMessage?.WinningQuote && (
        <CollectionOrderTotal price={lastJsonMessage?.WinningQuote} />
      ),
    [lastJsonMessage?.WinningQuote]
  );

  const CancelOrderButtonPressed = () => {
    setCancelOrderModalVisible(true);
  };

  const CancelOrderConfirmButtonPressed = () => {
    setCancelOrderLoading(true);
    cancelOrderCreation(
      { orderId: id },
      () => {
        setCancelOrderModalVisible(false);
        dispatch(GetCollectionOrderDetail(id));
        setCancelOrderLoading(false);
      },
      (err) => {
        setCancelOrderErrorModalText(err);
        setCancelOrderErrorModalVisible(true);
        setCancelOrderLoading(false);
      }
    );
  };

  const renderActionButtons = () => {
    return (
      <Grid className={classes.actionButtonsContainer}>
        {(showCancelBtn && is_able_to("reject_orders")) ? (
          <>
            <OrderRejectButton
              title="Cancel order"
              onClick={CancelOrderButtonPressed}
              loading={cancelOrderLoading}
            />
          </>
        ) : (
          <></>
        )}
        {isAdmin &&
          <CollectionOrderHeaderActions data={data} />
        }
      </Grid>
    )
  };

  function renderGetPrice() {
    if (data.Status === "OpenPremium" && role === "Admin") {
      return (
        <>
          <Box my={3}>
            <NewCollectionOrderTotal
              source={CollectionOrderTotalSource.Detail}
              placeOrderLoading={placeOrderLoading}
              setGetPriceLoading={setGetPriceLoading}
              getPriceLoading={getPriceLoading}
              orderId={data.Id}
              handleError={(err) => { }}
              onGetPrice={onGetPrice}
              onCourierChange={(e: any) => {
                setQuoteIds(e.ids);
                setMaxPriceQuoteId(e.maxPriceQuoteId);
              }}
              onSubmit={(id: string) => {
                setQuoteIds([id] as never);
                handlePlaceOrder();
              }}
              maxPriceQuoteId={maxPriceQuoteId}
              setMaxPriceQuoteId={setMaxPriceQuoteId}
              isValid={true}
            />
            {quoteIds.length > 0 && (
              <Box
                sx={{
                  margin: "24px 24px 0 0",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <PrimaryButton
                  title="Book Delivery"
                  disabled={placeOrderLoading}
                  style={{ width: 148, height: 40 }}
                  onClick={handlePlaceOrder}
                />
              </Box>
            )}
          </Box>
        </>
      );
    }
    return null;
  }

  return (
    <Container className={classes.root}>
      {loading ? (
        <Grid
          item
          container
          justifyContent="center"
          alignItems="center"
          style={{ marginTop: "96px" }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        data && (
          <>
            {role === "Admin" ? (
              <CollectOrderCanceledModal
                referenceNumber={data.ReferenceNumber}
                isOpen={showCanceledModal}
                onClose={() => window.location.reload()} />
            ) : null}

            <CancelCollectOrderModal
              visible={cancelOrderModalVisible}
              loading={cancelOrderLoading}
              onClose={() => setCancelOrderModalVisible(false)}
              onRejectButtonPressed={
                CancelOrderConfirmButtonPressed
              }
              data={data}
            />
            <ErrorModal
              visible={cancelOrderErrorModalVisible}
              title="Error"
              buttonTitle="Dismiss"
              errorBody={cancelOrderErrorModalText}
              onClose={() => {
                setCancelOrderErrorModalText("");
                setCancelOrderErrorModalVisible(false);
              }}
            />
            <CollectionOrderHeader
              id={data.Id}
              isAdmin={isAdmin}
              isMerchant={role === "Merchant"}
              title={data.ReferenceNumber}
              portalStatus={data.PortalStatus}
              headerRight={isAdmin || showActionButtons ? renderActionButtons : undefined}
              premiumSelected={Boolean(data.PremiumSelected)}
              from={urlParams.get("from")}
              manuallyResolved={isAdmin && data.ManuallyResolved}
            />
            <div style={{ marginTop: "24px" }}>
              {renderGetPrice()}
              <CollectionOrderDeliveryInfo
                orderDate={data.Drafted}
                token={data.ChatJwtToken}
                orkestroOrderId={data.OrkestroOrderId}
                deliveryAddress={getMultipleValuesAsText([
                  data.DropOffAddressLine1,
                  data.DropOffAddressLine2,
                  data.DropOffAddressLine3,
                ])}
                deliveryInstructions={data.CustomerInstructions}
                deliveryType={data.DeliveryTypeKey}
                socketMessage={lastJsonMessage}
                showPremiumWarning={
                  role !== "Admin" &&
                  data.PremiumSelected &&
                  !data.Placed
                }
              />
              {data.Delivered && renderProofOfDelivery()}
              <CollectionOrderBasicInfo data={data} />

              {role === "Admin" ? (
                <CollectionOrderHistory data={data} />
              ) : null}

              {renderCustomerInfo()}
              {renderPricing()}
            </div>
          </>
        )
      )}
    </Container>
  );
}